import React from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames/bind';

import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import BasicIconButton from '@COMPONENTS/COMMON/buttons/BasicIconButton';

import styles from './EntityTable.module.scss';

const cx: CX = classnames.bind(styles);

interface Column<T> {
    key: keyof T;
    header: string;
    render?: (value: any, item: T) => React.ReactNode;
}

interface EntityTableProps<T> {
    data: T[];
    columns: Column<T>[];
    onEdit: (item: T) => void;
    onDelete: (item: T) => void;
    onCreate: () => void;
    isLoading?: boolean;
    entityName: string;
}

function EntityTable<T extends { id: number; name: string }>(props: EntityTableProps<T>) {
    const {
        data, columns, onEdit, onDelete, onCreate, isLoading, entityName,
    } = props;

    if (isLoading) {
        return (
            <div className={cx('loading')}>
                Loading
                {' '}
                {entityName.toLowerCase()}
                ...
            </div>
        );
    }

    return (
        <div className={cx('entity-table')}>
            <div className={cx('table-header')}>
                <h3>
                    Manage
                    {' '}
                    {entityName}
                </h3>
                <div className={cx('add-button-container')}>
                    <BasicButton
                        title={`Add ${entityName}`}
                        variant="primary"
                        size="medium"
                        onClick={onCreate}
                        style={{
                            maxWidth: '200px',
                        }}
                    />
                </div>
            </div>

            <div className={cx('table-container')}>
                <table className={cx('table')}>
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th key={String(column.key)} className={cx('table-header-cell')}>
                                    {column.header}
                                </th>
                            ))}
                            <th className={cx('table-header-cell', 'actions-column')}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + 1} className={cx('empty-state')}>
                                    No
                                    {' '}
                                    {entityName.toLowerCase()}
                                    {' '}
                                    found. Click &quot;Add
                                    {' '}
                                    {entityName}
                                    &quot; to create one.
                                </td>
                            </tr>
                        ) : (
                            data.map((item) => (
                                <tr key={item.id} className={cx('table-row')}>
                                    {columns.map((column) => (
                                        <td key={String(column.key)} className={cx('table-cell')}>
                                            {column.render
                                                ? column.render(item[column.key], item)
                                                : String(item[column.key] ?? '')}
                                        </td>
                                    ))}
                                    <td className={cx('table-cell', 'actions-cell')}>
                                        <div className={cx('action-buttons')}>
                                            <BasicIconButton
                                                icon="edit"
                                                tooltip="Edit"
                                                width={16}
                                                height={16}
                                                onClick={() => onEdit(item)}
                                                color="#353944"
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    color: '#353944',
                                                    border: '1px solid #353944',
                                                    marginRight: '0px',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                            <BasicIconButton
                                                icon="delete"
                                                tooltip="Delete"
                                                width={16}
                                                height={16}
                                                onClick={() => onDelete(item)}
                                                color="#F04438"
                                                className="delete-button"
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    color: '#F04438',
                                                    border: '1px solid #F04438',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// PropTypes for runtime validation (required for legacy code compatibility)
EntityTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        header: PropTypes.string.isRequired,
        render: PropTypes.func,
    })).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCreate: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    entityName: PropTypes.string.isRequired,
};

EntityTable.defaultProps = {
    isLoading: false,
};

export default EntityTable;
